import makeStyles from '@mui/styles/makeStyles';
import {Video} from "lucide-react";
import {find, get, isEmpty, map} from "lodash";
import moment from "moment";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useHistory} from "react-router-dom";
import {curriculumSessionList} from "business/curriculumSession";
import {groupSessionsByTheme, hasThemes, orderByTheme} from "business/curriculumTheme";
import {mentorshipGroupChannel} from "business/channel";
import {
  callDateParts,
  isCallLive,
  sessionForCall,
  sessionToSchedule,
  upcomingCallForSession,
  upcomingCalls,
} from "business/upcomingCalls";

import {color, font, space, type} from "common/tokens";
import useFreshData, {dataKeyFor, dataKeys} from "common/useFreshData";
import BookingConfirmation from "components/redesign/BookingConfirmation";
import CallSlotsCard from "components/redesign/CallSlotsCard";
import CtaButton from "components/redesign/CtaButton";
import EventSeriesDialog, {singleOption} from "components/EventSeriesDialog";
import CurriculumSessionsPreview from "components/redesign/CurriculumSessionsPreview";
import ScheduleCallCard from "components/redesign/ScheduleCallCard";
import WaitingForMenteesCard from "components/redesign/WaitingForMenteesCard";
import LeaderboardCard from "components/redesign/LeaderboardCard";
import MilestoneStepper from "components/redesign/MilestoneStepper";
import NextCallHero from "components/redesign/NextCallHero";
import RosterCard from "components/redesign/RosterCard";
import CongratulationsWindow from "components/CongratulationsWindow";
import WalkthroughCard from "components/redesign/WalkthroughCard";
import EventList from "components/redesign/EventList";
import Skeleton from "components/redesign/Skeleton";

const useStyles = makeStyles(theme => ({
  page: {
    background: color.SURFACE,
    minHeight: '100%',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '48px 32px 64px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      padding: '24px 16px 48px',
    },
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 529px) minmax(0, 437px)',
    justifyContent: 'space-between',
    gap: 40,
    alignItems: 'start',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 46,
  },
  upcoming: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  viewAllEvents: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    color: color.ACCENT,
    textDecoration: 'none',
    alignSelf: 'flex-start',
  },
  callHint: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: 0,
    flexBasis: '100%',
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    gap: 25,
  },
  // Stands in for the hero and the call list until the events land, so the page
  // never claims there are no calls before it has looked.
  heroSkeleton: {
    borderRadius: 14,
  },
  callSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
}));

// The rail is a glance at what is next, not the calendar — All Events carries
// the rest.
const UPCOMING_CALL_LIMIT = 3;

const MentorshipHome = ({
  mentorshipId,
  mentorshipFormedDialogPendingFor,
  dismissMentorshipFormedDialog,
  events,
  eventsLoaded,
  sessions,
  members,
  self,
  selfPicture,
  chatChannels,
  mentorGroup,
  groupMentorshipId,
  profileId,
  mentorshipGroupId,
  callSlots,
  suggestedCallTimes,
  isMentor,
  menteeCount,
  initMentorship,
  initMentorshipSessions,
  loadEvents,
  getChatChannels,
  toggleMessageWindow,
  refreshCallSlots,
  addSlot,
  removeSlot,
  bookCall,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const {t} = useTranslation();
  const [booked, setBooked] = useState(false);
  const [pickedSessionId, setPickedSessionId] = useState(null);
  const [clash, setClash] = useState(null);

  useFreshData(dataKeys.MENTORSHIP_SESSIONS, initMentorshipSessions);
  useFreshData(
    dataKeyFor(dataKeys.MENTORSHIP, mentorshipId),
    () => Promise.all([initMentorship(mentorshipId), loadEvents(mentorshipId), refreshCallSlots(mentorshipId)]),
    {enabled: Boolean(mentorshipId)}
  );

  const calls = upcomingCalls(events);
  const nextCall = calls[0];
  const suggestions = get(suggestedCallTimes, 'suggestions', []);

  const statuses = curriculumSessionList(
    get(sessions, 'ongoingSessions'),
    get(sessions, 'allSessions')
  );
  // A curriculum has either themes or sessions attached directly, never both.
  const themed = hasThemes(statuses);
  const themes = themed ? groupSessionsByTheme(statuses) : [];

  // Open the group conversation the pair manager is in, matching the Android
  // app. Channels are otherwise only loaded by the Chat tab, so fetch them
  // first; without a match we fall back to the Chat list.
  const openGroupChat = async () => {
    const channels = get(chatChannels, 'length') ? chatChannels : await getChatChannels();
    const channel = mentorshipGroupChannel(channels || chatChannels, {
      mentorGroup,
      mentorship: groupMentorshipId,
    });
    const channelId = get(channel, 'channelId');
    if (channelId) {
      await toggleMessageWindow(true, channelId);
    }
    history.push('/chat');
  };

  const openSession = sessionStatus =>
    history.push(`/mentorship/${get(sessionStatus, 'session.id')}/summary`);

  const hasEvents = !isEmpty(events);
  const scheduleSession =
    find(statuses, s => get(s, 'session.id') === pickedSessionId) ||
    sessionToSchedule(statuses, events);
  const sessionTitle = get(scheduleSession, 'session.title');

  const bookSuggestion = async (suggestion, sessionName) => {
    const title = sessionName || t('mentoringCall');
    await bookCall(mentorshipId, {
      title,
      session_name: title,
      description: '',
      scheduled_time: suggestion.scheduledTime,
      created_by: profileId,
      is_pair_group_call: true,
      ...(mentorshipGroupId && {mentor_group: mentorshipGroupId}),
    });
    setPickedSessionId(null);
    setBooked(true);
  };

  // Another member may have booked this session since the page loaded; nothing
  // pushes that here, so check the latest events before booking.
  const scheduleCall = async suggestion => {
    const latest = await loadEvents(mentorshipId);
    const existing = upcomingCallForSession(latest, sessionTitle);
    if (existing) {
      setClash({suggestion, existing, sessionName: sessionTitle});
      return;
    }
    await bookSuggestion(suggestion, sessionTitle);
  };

  const bookAnyway = async () => {
    const {suggestion, sessionName} = clash;
    setClash(null);
    await bookSuggestion(suggestion, sessionName);
  };

  const openCallVideo = call => {
    const uuid = get(call, 'videos[0].sessionUuid');
    if (uuid) {
      history.push(`/video/join-meeting/${uuid}?section=video`);
    }
  };

  // A call is opened in place: the link only goes live ten minutes before it,
  // so until then the row offers the session material instead of a join.
  const callDetails = call => {
    const session = sessionForCall(call, statuses);
    const live = isCallLive(call);
    return (
      <>
        <CtaButton
          size={'control'}
          variant={live ? 'info' : 'secondary'}
          tone={live ? undefined : 'info'}
          disabled={!live}
          icon={<Video size={18} strokeWidth={2}/>}
          label={t('joinCall')}
          onClick={() => openCallVideo(call)}
        />
        {Boolean(session) && (
          <CtaButton
            size={'control'}
            variant={'info'}
            label={t('reviewSessionMaterial')}
            onClick={() => openSession(session)}
          />
        )}
        <p className={classes.callHint}>{t(live ? 'callLinkActive' : 'callLinkAvailableHint')}</p>
      </>
    );
  };

  return (
    <div className={classes.page}>
      <div className={`${classes.container} ui-enter`}>
        <div className={classes.columns}>
          <div className={classes.main}>
            {eventsLoaded ? (
              <>
                {/* Before any event exists the mentor's short list has set the
                    times, so a mentee books the first call. */}
                {!nextCall && suggestions.length && isMentor && !hasEvents ? (
                  <WaitingForMenteesCard menteeCount={menteeCount}/>
                ) : !nextCall && suggestions.length ? (
                  <ScheduleCallCard
                    suggestions={suggestions}
                    sessionTitle={sessionTitle}
                    firstCall={!hasEvents}
                    onSchedule={scheduleCall}
                    sessionOptions={map(orderByTheme(statuses), ({session}) => ({key: session.id, label: session.title}))}
                    sessionKey={get(scheduleSession, 'session.id')}
                    onChangeSession={hasEvents ? setPickedSessionId : undefined}
                  />
                ) : (
                  <NextCallHero
                    call={nextCall}
                    session={sessionForCall(nextCall, statuses)}
                    onJoin={() => openCallVideo(nextCall)}
                    onReviewSession={openSession}
                    onBookEvent={() => history.push('/mentorship/events', {openEventForm: true})}
                    onViewAllEvents={() => history.push('/mentorship/events')}
                  />
                )}
                <div className={classes.upcoming}>
                  <EventList
                    label={t('upcomingCalls')}
                    events={calls.slice(1, 1 + UPCOMING_CALL_LIMIT)}
                    renderDetails={callDetails}
                    meta={event => `${callDateParts(event).time} · ${t('withYourGroup')}`}
                  />
                  {/* Outside the list, which renders nothing when there is no
                      second call. With nothing upcoming and no suggestions the
                      empty card carries it. */}
                  {hasEvents && Boolean(nextCall || suggestions.length) && (
                    <Link to={'/mentorship/events'} className={classes.viewAllEvents}>
                      {t('viewAllEvents')}
                    </Link>
                  )}
                </div>
              </>
            ) : (
              <>
                <Skeleton height={168} className={classes.heroSkeleton}/>
                <div className={classes.callSkeleton}>
                  <Skeleton width={140} height={17}/>
                  <Skeleton height={64}/>
                </div>
              </>
            )}
            {themed ? (
              <MilestoneStepper themes={themes}/>
            ) : (
              <CurriculumSessionsPreview
                sessionStatuses={statuses}
                selfPicture={selfPicture}
                onOpenSession={openSession}
              />
            )}
          </div>

          <div className={classes.rail}>
            <RosterCard
              members={members}
              onMessageGroup={openGroupChat}
            />
            <LeaderboardCard
              ongoingSessions={get(sessions, 'ongoingSessions')}
              self={self}
              members={members.map(member => ({
                userId: get(member, 'id'),
                name: `${get(member, 'firstName', '')} ${get(member, 'lastName', '')}`.trim(),
              }))}
            />
            <CallSlotsCard
              callSlots={callSlots}
              onAdd={slot => addSlot(mentorshipId, slot)}
              onRemove={slotId => removeSlot(mentorshipId, slotId)}
            />
            <WalkthroughCard/>
          </div>
        </div>
      </div>

      {booked && <BookingConfirmation onClose={() => setBooked(false)}/>}

      <EventSeriesDialog
        open={Boolean(clash)}
        title={t('sessionAlreadyBookedTitle')}
        prompt={t('sessionAlreadyBooked', {
          session: get(clash, 'sessionName'),
          when: moment(get(clash, 'existing.scheduledTime')).format('dddd, D MMMM, h:mm A'),
        })}
        options={singleOption(t('bookAnyway'))}
        onChoose={bookAnyway}
        onDismiss={() => setClash(null)}
      />

      {/* Matching closes the moment the mentorship forms, taking its own
          dialog with it, so the congratulation lands here instead. */}
      <CongratulationsWindow
        formed
        open={Boolean(mentorshipId) && mentorshipFormedDialogPendingFor === mentorshipId}
        handleClose={dismissMentorshipFormedDialog}
      />
    </div>
  );
};

export default MentorshipHome;
